import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import React, { memo, isValidElement } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlock } from './code-block';
import { generateMarkdownHeaderId } from '@/hooks/useMarkdownHeaders';
import { dedentParagraphsAfterLists } from '@/lib/markdown/dedent-after-lists';
const components: Partial<Components> = {
  // @ts-expect-error
  code: CodeBlock,
  pre: ({
    children
  }) => <>{children}</>,
  p: ({
    children
  }) => {
    // If children is an array, check if it contains pre or div elements
    if (Array.isArray(children)) {
      const hasBlockElements = children.some(child => isValidElement(child) && (child.type === 'pre' || child.type === 'div'));

      // If it contains block elements, return children directly without p wrapper
      if (hasBlockElements) {
        return <>{children}</>;
      }
    }

    // Otherwise, render as normal paragraph
    return <p className="whitespace-pre-wrap">{children}</p>;
  },
  ol: ({
    node,
    children,
    ...props
  }) => {
    return <ol className="list-decimal list-outside ml-4" {...props}>
        {children}
      </ol>;
  },
  li: ({
    node,
    children,
    ...props
  }) => {
    return <li className="py-1" {...props}>
        {children}
      </li>;
  },
  ul: ({
    node,
    children,
    ...props
  }) => {
    return <ul className="list-decimal list-outside ml-4" {...props}>
        {children}
      </ul>;
  },
  strong: ({
    node,
    children,
    ...props
  }) => {
    return <span className="font-semibold" {...props}>
        {children}
      </span>;
  },
  a: ({
    node,
    children,
    ...props
  }) => {
    return (
      // @ts-expect-error
      <Link className="text-blue-500 hover:underline" target="_blank" rel="noreferrer" {...props}>
        {children}
      </Link>
    );
  },
  h1: ({
    node,
    children,
    ...props
  }) => {
    return <h1 className="text-3xl font-semibold mt-6 mb-2" {...props}>
        {children}
      </h1>;
  },
  h2: ({
    node,
    children,
    ...props
  }) => {
    const id = generateMarkdownHeaderId(typeof children !== 'string' ? '' : children);
    return <h2 id={id} className="text-2xl font-semibold mt-6 mb-2" {...props}>
        {children}
      </h2>;
  },
  h3: ({
    node,
    children,
    ...props
  }) => {
    return <h3 className="text-xl font-semibold mt-6 mb-2" {...props}>
        {children}
      </h3>;
  },
  h4: ({
    node,
    children,
    ...props
  }) => {
    return <h4 className="text-lg font-semibold mt-6 mb-2" {...props}>
        {children}
      </h4>;
  },
  h5: ({
    node,
    children,
    ...props
  }) => {
    return <h5 className="text-base font-semibold mt-6 mb-2" {...props}>
        {children}
      </h5>;
  },
  h6: ({
    node,
    children,
    ...props
  }) => {
    return <h6 className="text-sm font-semibold mt-6 mb-2" {...props}>
        {children}
      </h6>;
  }
};
const remarkPlugins = [remarkGfm];
const NonMemoizedMarkdown = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = dedentParagraphsAfterLists(children);
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalized = t1;
  let t2;
  if ($[2] !== className || $[3] !== normalized) {
    t2 = <ReactMarkdown className={className} remarkPlugins={remarkPlugins} components={components}>{normalized}</ReactMarkdown>;
    $[2] = className;
    $[3] = normalized;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const Markdown = memo(NonMemoizedMarkdown, (prevProps, nextProps) => prevProps.children === nextProps.children);